import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { getLocalStorageItem } from '../../../../utils/helper';
import SuppressedPanel from './SuppressedPanel';
import LoadFailedPanel from './LoadFailedPanel';
import BarList from './CareBars';
import { CareFootnote } from './CareShell';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../../../component/common/designSystem';
import { PROGRAMME_PATHS, BREADTH_RAMP, SECTION_LABELS, programmeColor } from './careTheme';

/**
 * One clinical section's overview — Care Programmes or Reproductive Health.
 *
 * Both sections render this component. They are separate routes so a customer
 * can have one without the other, but the question each answers is identical:
 * is the benefit reaching anybody, and do the people it reaches come back?
 *
 * Three things carry the page:
 *
 *  1. HOW MANY PEOPLE ARE ENROLLED, de-duplicated across the section, so
 *     somebody on two programmes is one person and not two adoptions.
 *  2. WHETHER THEY CAME BACK. Enrolment on its own is the number that flatters
 *     a benefit — a programme everybody signs up to and abandons looks
 *     identical to a healthy one if you only count sign-ups. The active figure
 *     and the dormant figure sit next to each other for that reason.
 *  3. WHICH PROGRAMMES ARE CARRYING IT, with take-up measured against one
 *     shared denominator so the bars are genuinely comparable.
 *
 * WHAT IS NOT ON THIS PAGE, AND IS NOT REACHABLE FROM IT: anything anybody
 * recorded, and anybody's name. See companyCareApi.js for why.
 */
const SectionOverview = ({ section, fetcher, populationLabel = 'users', basePath }) => {
  const navigate = useNavigate();
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  if (loader) return <Loader />;

  // Checked before suppression: a failed request is not a small cohort.
  if (stats?.error) return <LoadFailedPanel />;

  // The whole page is withheld, not just a panel — at this size every figure on
  // it would be about identifiable people.
  if (stats?.suppressed) {
    return (
      <SuppressedPanel
        minimum={stats.minimum}
        what={`anything in ${SECTION_LABELS[section] || 'this section'}`}
      />
    );
  }

  const programmes = stats?.programmes || [];
  const reported = programmes.filter((row) => !row.suppressed);
  const withheld = programmes.filter((row) => row.suppressed);
  const root = basePath || `/${slug}/care`;

  const trendLength = reported[0]?.trend?.length || 0;
  const trendHasData = reported.some((row) => (row.trend || []).some((day) => day.checkIns > 0));

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: reported.map((row) => programmeColor(row.key)),
    stroke: { width: 3, curve: 'smooth' },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(trendLength), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  /**
   * Take-up per programme. Every bar shares the SAME denominator — the whole
   * population — so they are directly comparable, which separate donuts could
   * never be. The share is the backend's, not recomputed here.
   */
  const adoptionRows = reported.map((row) => ({
    label: row.label,
    count: row.participants,
    share: row.adoption,
    key: row.key
  }));

  return (
    <div>
      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Enrolled"
          value={formatBigNumber(stats?.participants || 0)}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title={`Share of ${populationLabel}`}
          value={stats?.totalUsers > 0 ? `${stats.adoption}%` : '-'}
        />
        {/* The pair that tells the truth about a benefit: signed up, and still
            turning up. Either one alone can be made to look good. */}
        <StatCard
          icon={STAT_ICONS.streak}
          title="Active This Month"
          value={formatBigNumber(stats?.activeUsers || 0)}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Check-ins (30d)"
          value={formatBigNumber(stats?.checkIns || 0)}
        />
        <StatCard
          icon={STAT_ICONS.newUsers}
          title="Joined This Month"
          value={formatBigNumber(stats?.joinedInWindow || 0)}
        />
      </div>

      {/* A partial total is labelled as one. Silently summing part of a section
          and calling it the total is how a chart starts lying quietly. */}
      {!stats?.totalsAreComplete ? (
        <p className="mt-3 text-xs text-shoorah-secondary dark:text-gray-400">
          The check-in, active and joined figures above cover {stats.programmesReported} of{' '}
          {stats.programmesTotal} programmes. The rest are withheld because too few people are
          enrolled, and they are left out of the totals rather than folded in — including them would
          let the withheld numbers be worked out by subtraction. The enrolled count is complete.
        </p>
      ) : null}

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ---------- Take-up ---------- */}
        <Panel
          title="Take-up by programme"
          subtitle={`Share of all ${populationLabel}, so the bars compare directly`}
        >
          {adoptionRows.length ? (
            <BarList
              rows={adoptionRows}
              unit="people"
              colorFor={(index, label, row) => programmeColor(row.key)}
            />
          ) : (
            <PanelEmpty>No programme in this section has enough people to report.</PanelEmpty>
          )}
        </Panel>

        {/* ---------- Breadth ---------- */}
        <Panel
          title="Programmes per person"
          subtitle="How many of this section's programmes one person uses"
        >
          {stats?.breadthSplit?.length ? (
            <BarList
              rows={stats.breadthSplit}
              unit="people"
              colorFor={(index) => BREADTH_RAMP[index] || CHART_COLORS.gray}
            />
          ) : (
            <PanelEmpty>
              {withheld.length
                ? 'Withheld while a programme in this section is too small to report — the spread plus a partial total would narrow down the hidden one.'
                : 'Nobody is enrolled in anything here yet.'}
            </PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Trend ---------- */}
      <div className="mt-4">
        <Panel title="Check-ins over the last 30 days" subtitle="One line per programme">
          {trendHasData ? (
            <Chart
              options={trendOptions}
              series={reported.map((row) => ({
                name: row.label,
                data: (row.trend || []).map((day) => day.checkIns || 0)
              }))}
              type="line"
              height={300}
            />
          ) : (
            <PanelEmpty>
              People are enrolled, but nobody has checked in during the last 30 days.
            </PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Comparison ---------- */}
      <div className="mt-4">
        <Panel
          title="Programmes side by side"
          subtitle="Enrolled, still active, and how the two compare"
        >
          {reported.length ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Programme</th>
                  <th className="py-2 px-4 font-medium text-center">Enrolled</th>
                  <th className="py-2 px-4 font-medium text-center">Take-up</th>
                  <th className="py-2 px-4 font-medium text-center">Active (30d)</th>
                  <th className="py-2 px-4 font-medium text-center">Dormant</th>
                  <th className="py-2 px-4 font-medium text-center">Still active</th>
                  <th className="py-2 font-medium text-right">Check-ins</th>
                </>
              }
            >
              {reported.map((row) => (
                <tr
                  key={row.key}
                  className={`${PANEL_ROW_CLASS} cursor-pointer`}
                  onClick={() => navigate(`${root}/${PROGRAMME_PATHS[row.key] || row.key}`)}
                >
                  <td className="py-2 pr-4">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: programmeColor(row.key) }}
                      />
                      {row.label}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.participants)}</td>
                  <td className="py-2 px-4 text-center">{row.adoption}%</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.activeUsers)}</td>
                  {/* Sits beside the active count on purpose: enrolled-and-gone
                      is the number a benefits team can actually act on. */}
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.dormant)}</td>
                  <td className="py-2 px-4 text-center">{row.activeRate}%</td>
                  <td className="py-2 text-right">
                    {formatBigNumber(row.checkIns)}
                    <span className="ml-1.5 text-gray-400 font-normal">{row.unit}</span>
                  </td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>Nothing in this section clears the reporting threshold.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* Named, but with no figure beside them. Leaving a withheld programme off
          the page entirely would read as "we don't offer that". */}
      {withheld.length ? (
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          Withheld in this view: {withheld.map((row) => row.label).join(', ')}. Fewer than{' '}
          {stats.minimum} people in the current scope are enrolled, so no figures are shown for
          {withheld.length === 1 ? ' it' : ' them'} — not even the count.
        </p>
      ) : null}

      <CareFootnote minimum={stats?.minimum} />
    </div>
  );
};

SectionOverview.propTypes = {
  section: PropTypes.oneOf(['care', 'reproductive']).isRequired,
  fetcher: PropTypes.func.isRequired,
  populationLabel: PropTypes.string,
  basePath: PropTypes.string
};

export default SectionOverview;
